import { ModelType } from 'models/ModelType';
import {
  APIComponentAttributes,
  APICurrentUser,
  APIEntityType,
  APIFieldAttributes,
  APIFieldType,
  APIReferenceAttributes,
  APIReferenceType,
  ArdoqId,
  Organization,
  PermissionRole,
  PersonalSetting,
  ViewIds,
  FilterAttributes,
  CalculatedFieldSettings,
  LocallyDerivedTransformation,
  InventoryRecentColumnSelectionPerWorkspaceOrTypePersonalSettings,
  WorkspaceFilterAttributes,
} from '@ardoq/api-types';
import { ReferenceAdjacency } from '@ardoq/data-model';
import type { AttributeComparator } from '@ardoq/filter-interface';
import { MouseEvent, FormEvent } from 'react';
import Backbone from 'backbone';

export type VersionedId = {
  readonly _id: ArdoqId;
  readonly _version: number;
};

export enum Selected {
  SELECTED,
  NOT_SELECTED,
  MIXED,
}

export enum ModelStructureType {
  FLEXIBLE,
  RIGID,
}

export enum FieldPopulateMethod {
  MANUAL = 'manual',
  CALCULATED = 'calculated',
  LOCALLY_DERIVED = 'locallyDerived',
}

export const fieldTypesWithCalculatedTransformation = new Set([
  APIFieldType.CHECKBOX,
  APIFieldType.DATE_ONLY,
  APIFieldType.DATE_TIME,
  APIFieldType.EMAIL,
  APIFieldType.LIST,
  APIFieldType.NUMBER,
  APIFieldType.TEXT,
  APIFieldType.TEXT_AREA,
  APIFieldType.URL,
]);

export const fieldTypesWithLocallyDerivedTransformation = new Set([
  APIFieldType.NUMBER,
  APIFieldType.TEXT,
  APIFieldType.LIST,
]);

export const listFieldTypes = [
  APIFieldType.LIST,
  APIFieldType.SELECT_MULTIPLE_LIST,
  'SelectMultiple', // legacy for models/field.js
];

// Backbone models
export interface BasicModelValidationEntry {
  pattern?: RegExp;
  required?: boolean;
  msg?: string;
}
export interface BasicModel extends Backbone.Model {
  getId: () => ArdoqId;
  name: () => string;
  getLock: () => boolean;
  getDescription: () => string;
  editing?: boolean;
  // validationErrors is used on Workspaces, Components and Entities
  validationErrors?: any[] | null;
  lastChangeTime?: number; // Exists on components, references and workspaces
  validation?: Record<
    string,
    BasicModelValidationEntry | BackboneFormValidatorFunction
  >;
}

export type GetRefTypeResult = APIReferenceType | 'null';
export interface ReferenceTypeInfo {
  val: number | string;
  label: string;
}
export type InferenceBackboneModel = Backbone.Model;
export type TraversalBackboneModel = SyncableBackboneModel;

/**
 * @deprecated
 * This type was created to have a type that was as narrow as possible to ensure that websocket events were handled correctly.
 * https://github.com/ardoq/ardoq-packages/pull/13569#discussion_r1778197128
 */
export type SyncableBackboneModel = Backbone.Model;

export interface ComponentBackboneModel extends BasicModel {
  getChildren: (ignoreSort?: boolean) => ComponentBackboneModel[];
  getParent: () => ComponentBackboneModel | null;
  getComponentTypeName: () => string;
  getValidComponentType: () => ModelType | null;
  handleChangeParent: VoidFunction;
  setComponentType: VoidFunction;
  setDefaultValues: (props: Record<string, any>) => void;
  getValidComponentTypeByParent: () => ModelType | null;
}

export type DateRangePropertyValue = {
  start: string | null;
  end: string | null;
};

export type PropertyValue =
  | string
  | number
  | boolean
  | Date
  | DateRangePropertyValue
  | LocallyDerivedTransformation[]
  | string[]
  | Record<string, unknown>
  | null;

export type PermissionOrgRole = PermissionRole;

export type PerspectiveBackboneModel = Backbone.Model & {
  hasContent: () => boolean;
};

export interface Tag extends BasicModel {}

export type WorkspaceSurvey = {
  readonly id: ArdoqId;
  readonly name: string;
  readonly live: boolean;
};
export interface WorkspaceStartViewOption {
  label: string;
  val: ViewIds | null;
}

export interface FieldBackboneModel extends Backbone.Model {
  name: () => string;
  getLabel: () => string;
  getRawLabel: () => string;
  getType: () => APIFieldType;
  getAllowedValues: () => string[];
  getModelId: () => ArdoqId;
  getCalculatedFieldSettings: () => CalculatedFieldSettings | undefined;
  getComponentTypes: () => ArdoqId[];
  getReferenceTypes: () => ArdoqId[];
  isListType: () => boolean;
  isCalculated: () => boolean;
  isLocallyDerived: () => boolean;
  isComponentField: () => boolean;
  isReferenceField: () => boolean;
  removeComponentType: (typeId: string) => void;
  removeReferenceType: (typeId: string) => void;
  format: (
    value: any,
    attributes?: any,
    escapeHTML?: boolean,
    excludeTime?: boolean
  ) => string | number | undefined;
  getRawDescription: () => string;
  getDescription: () => string;
  hasComponentType: (type: string) => boolean;
  hasReferenceType: (type: string) => boolean;
  numberFormatOptions?: string;
  toJSON: () => APIFieldAttributes;
  isMultiselect: () => boolean;
  isFormatHtmlEscaped: () => boolean;
  lastAllowedValues: [defaultValue: string, result: string[]];
}

/**
 * This is a global field definition.
 * It contains the attributes that are shared across fields of the same name
 * across the org.
 */
export interface GlobalFieldAttributes {
  type: APIFieldType;
  label: string;
  name: string;
  defaultValue: any;
  calculatedFieldSettings?: { storedQueryId: string };
  numberFormatOptions?: string;
}

export type FieldEditorOptions = {
  readonly label: string;
  readonly presetTypes: { isComponent: boolean; ids: string[] };
};

// Utility Types
export type OnClick = (event: MouseEvent<HTMLElement>) => void;
export type OnInput = (event: FormEvent<HTMLInputElement>) => void;

export interface LabeledValue<T = any> {
  value: T;
  label: string;
}

export interface CompElementExtension {
  comp: ComponentBackboneModel;
}

export interface IntegrationElementExtension {
  integration: { id: ArdoqId };
}

export interface MaximumPathLengths {
  maxIncomingPathLength?: number;
  maxOutgoingPathLength?: number;
}
export interface SaveAllChangedModels {
  (forced: boolean): void;
}

export interface TagCollection extends Backbone.Collection<Tag> {}

export interface PerspectiveFilters {
  advancedFilters: FilterAttributes[];
  workspaceFilter: null | FilterAttributes;
}

export interface LoadFiltersOptions {
  workspaceFilter?: WorkspaceFilterAttributes;
  advancedFilters?: FilterAttributes[];
  shouldTriggerChangeEvent: boolean;
}
export interface HasShouldTriggerChangeEvent {
  shouldTriggerChangeEvent?: boolean;
}

export declare class Filter extends Backbone.Model {
  getAvailableComparators: () => AttributeComparator[];
  getComparator: () => AttributeComparator | undefined;
  isIncluded: (
    entityType: APIEntityType,
    attributes: APIComponentAttributes | APIReferenceAttributes,
    fields: APIFieldAttributes[],
    referenceAdjacency: ReferenceAdjacency,
    referenceTypeName: string | null
  ) => boolean;
  isComponentFormatting: () => boolean;
  isReferenceFormatting: () => boolean;
  encodeURI: () => string;
  isDynamic: () => boolean;
  setRules: (rules: Filter[]) => void;
  isAffected: (entityType: APIEntityType) => boolean;
  isAttributeIncluded: (
    entityType: APIEntityType,
    attributes: APIComponentAttributes | APIReferenceAttributes,
    fields: APIFieldAttributes[],
    referenceAdjacency: ReferenceAdjacency,
    referenceTypeName: string | null
  ) => boolean;
  isTagIncluded: (entityType: APIEntityType, id: ArdoqId) => boolean;
  isCompLabelFilter: () => boolean;
  isRefLabelFilter: () => boolean;
  isWorkspaceFilter: () => boolean;
}

export interface ComponentHierarchyInterface {
  getChildren: (component: ComponentBackboneModel) => ComponentBackboneModel[];
  hasChildren: (component: ComponentBackboneModel) => boolean;
  clearWorkspaceHierarchy: (workspaceId: ArdoqId) => void;
  clearHierarchyForComponents: (components: ComponentBackboneModel[]) => void;
  clearAllHierarchies: () => void;
}

export type EditorModelExtension<T extends Backbone.Model = Backbone.Model> =
  T & {
    canHandleInvalid?: boolean;
  };

export interface BackboneFormValidatorError {
  type: any;
  message: string;
}

export type BackboneFormValidatorFunction = (
  value: any
) => boolean | BackboneFormValidatorError;

export type ValidatorFunction = (value: any) => null | string;

export interface TokenBackboneModel extends BasicModel {
  isAutogenerated: () => boolean;
}

export interface UserBackboneModel extends BasicModel {
  hasWriteAccess: () => boolean;
  isOrgAdmin: (orglabel?: string) => boolean;
  getOrganization: () => Organization;
}

export type ComponentTypeName = string;
export type ReferenceTypeName = string;
/**
 * Source Component Type Name -> Target Component Type Name -> Reference Type Name
 */
export type LastUsedReferenceTypeTriple = Record<
  ComponentTypeName,
  Record<ComponentTypeName, ReferenceTypeName>
>;

export interface GetPersonalSetting {
  (key: PersonalSetting.LOCALE): string;
  (key: PersonalSetting.DEFAULT): { digest: string };
  (key: PersonalSetting.LAST_VIEWED_DASHBOARD): ArdoqId;
  (key: PersonalSetting.ENFORCE_FILTERS_DIALOG): boolean;
  (
    key: PersonalSetting.LAST_USED_REFERENCE_TYPE_NAME_TRIPLES
  ): LastUsedReferenceTypeTriple;
  (key: PersonalSetting.SHOWN_EMAIL_PREFERENCES): boolean;
  (key: PersonalSetting.HIDE_MODEL_PREVIEW): boolean;
  (key: PersonalSetting.HIDE_ON_CLOSE_SCENARIO_WARNING): boolean;
  (key: PersonalSetting.DISABLE_REFERENCE_TIPS): boolean;
  (
    key: PersonalSetting.INVENTORY_RECENT_COLUMN_SELECTION_PER_WORKSPACE_OR_TYPE
  ): InventoryRecentColumnSelectionPerWorkspaceOrTypePersonalSettings;
  (key: PersonalSetting.USE_CASE_UPDATES_SEEN): ArdoqId[] | undefined;
  (key: PersonalSetting.RECENTLY_USED_VIEW_STYLE): ViewIds | undefined;
  (key: PersonalSetting | string): any;
}

export type SetPersonalSetting = (
  key: PersonalSetting | string,
  value: unknown
) => void;

export interface CurrentUserBackboneModel extends UserBackboneModel {
  getPersonalSetting: GetPersonalSetting;
  setLocalPersonalSetting: SetPersonalSetting;
  isTrial: () => boolean;
  canUnlock: (model: BasicModel) => boolean;
  organization?: Organization;
  organizations?: unknown[];
  loaded: boolean;
  isArdoqEmployee: () => boolean;
  toJSON: () => APICurrentUser;
}

export interface Model extends Backbone.Model {
  getReferenceTypes: () => Record<string | number, APIReferenceType>;
  getReferenceTypeById: (
    id: string | number,
    options?: { skipDeletedTypes?: boolean }
  ) => APIReferenceType | undefined;
  name: () => string;
  _flattenedModel?: Record<string, ModelType>;
  _parentList?: Record<string, Record<string, ModelType>>;
  fixModel: (children?: ModelType) => void;
  defaultReferenceTypes: Record<number, APIReferenceType>;
  _internalModelChanged: 'internal:ModelChanged';
}

export interface ModelIcon {
  label: string;
  category: string;
  representation: string;
  value: string;
  isSelected: boolean;
}

export interface ShapeSelectListItem {
  label: string;
  value: string;
  category: string;
  dataContent: string;
  isSelected: boolean;
}
export interface FolderBackboneModel extends BasicModel {
  getParent: () => FolderBackboneModel | null | undefined;
}

export interface Attachment extends BasicModel {
  isImage: () => boolean;
  getURI: () => string;
}

export enum NamedStackPage {
  MERGE_FLOW = 'mergeFlow',
  ENTITY_MERGE_FLOW = 'entityMergeFlow',
}
